import React from 'react';
import { NavLink, Link, withRouter } from 'react-router-dom';
import { Parse } from 'parse';
import { ThemeProvider } from '@mui/material/styles';
import { lifeTaggerTheme } from './LifeTaggerTheme';
import './Studio.css';
import { signOut } from '../sessions/signOut';

export function StudioIcon({ name, size = 20, ...props }) {
  const paths = {
    home: 'M3 10 12 3l9 7v10a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1Z',
    collection: 'm12 3 9 5-9 5-9-5 9-5Zm-9 9 9 5 9-5M3 16l9 5 9-5',
    signals:
      'M8 8a6 6 0 0 0 0 8m8-8a6 6 0 0 1 0 8M5 5a10 10 0 0 0 0 14M19 5a10 10 0 0 1 0 14M12 10v4',
    media: 'M4 3h16v18H4V3Zm0 13 5-5 4 4 3-3 4 4M15 7h.01',
    activity: 'M3 19h18M6 15V9m6 6V4m6 11v-5',
    settings:
      'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm-3-5h6l1 3 3 1 2 5-2 5-3 1-1 3H9l-1-3-3-1-2-5 2-5 3-1 1-3Z',
    link: 'm9 15 6-6M8 16l-2 2a4 4 0 0 1-5-5l5-5a4 4 0 0 1 5 0m2 0 2-2a4 4 0 0 1 6 6l-5 5a4 4 0 0 1-5 0',
    arrow: 'M5 12h14m-6-6 6 6-6 6',
    plus: 'M12 5v14M5 12h14',
    search: 'M10 3a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm5 12 6 6',
    grid: 'M3 3h7v7H3V3Zm11 0h7v7h-7V3ZM3 14h7v7H3v-7Zm11 0h7v7h-7v-7Z',
    list: 'M8 5h13M8 12h13M8 19h13M3 5h.01M3 12h.01M3 19h.01',
    place:
      'M12 22s8-9 8-14A8 8 0 0 0 4 8c0 5 8 14 8 14Zm0-17a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
    people:
      'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm8 1a4 4 0 0 1 0 7m2 4a4 4 0 0 1 3 4v2',
    spatial: 'm12 2 10 6v8l-10 6-10-6V8l10-6Zm0 10v10M2 8l10 4 10-4',
    qr: 'M3 3h6v6H3V3Zm12 0h6v6h-6V3ZM3 15h6v6H3v-6Zm12 0h3v3h3v3h-6v-6Z',
    audio: 'M4 10v4m4-8v12m4-15v18m4-15v12m4-8v4',
    text: 'M5 4h14M12 4v16M8 20h8',
    close: 'm6 6 12 12M6 18 18 6',
    logout: 'M9 4H4v16h5M9 12h12m-5-5 5 5-5 5',
    help: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM9 8a3 3 0 1 1 5 2c-2 1-2 2-2 3m0 4h.01',
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d={paths[name] || paths.collection} />
    </svg>
  );
}

const navigation = [
  ['/dashboard', 'Overview', 'home'],
  ['/objects', 'Collection', 'collection'],
  ['/qrcode', 'LifeCodes', 'qr'],
  ['/beacons', 'Beacons', 'signals'],
  ['/media', 'Media', 'media'],
  ['/analytics', 'Insights', 'activity'],
];

function StudioShell({ children, location }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [signingOut, setSigningOut] = React.useState(false);
  const [signOutError, setSignOutError] = React.useState('');
  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    setSignOutError('');
    try {
      await signOut(Parse.User, (path) => window.location.replace(path));
    } catch (_) {
      setSignOutError('Could not sign out. Please try again.');
      setSigningOut(false);
    }
  };
  const user = Parse.User.current();
  const name =
    user?.get('name') ||
    user?.get('firstName') ||
    user?.get('username') ||
    'Your account';
  const title =
    navigation.find(([path]) => location.pathname.startsWith(path))?.[1] ||
    (location.pathname.startsWith('/user') ? 'Account settings' : 'Workspace');
  React.useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  return (
    <ThemeProvider theme={lifeTaggerTheme}>
      <div className={`lt-studio ${menuOpen ? 'lt-menu-open' : ''}`}>
        <aside className="lt-sidebar">
          <Link to="/dashboard" className="lt-brand">
            <img src="/images/studio-logo.png" alt="" />
            <span>
              LifeTagger<span className="lt-brand-caption">THE STUDIO</span>
            </span>
          </Link>
          <div className="lt-workspace-label">
            <span className="lt-workspace-avatar">L</span>
            <div>
              LifeTagger workspace<small>Your world, connected</small>
            </div>
          </div>
          <span className="lt-nav-caption">CREATE & CONNECT</span>
          <nav aria-label="Main navigation">
            {navigation.map(([path, label, icon]) => (
              <NavLink
                key={path}
                to={path}
                className="lt-nav-item"
                activeClassName="is-active"
              >
                <StudioIcon name={icon} />
                <span>{label}</span>
                {path === '/objects' && <span className="lt-nav-dot" />}
              </NavLink>
            ))}
          </nav>
          <div className="lt-sidebar-bottom">
            <div className="lt-sidebar-note">
              <span className="lt-small-spark">✧</span>
              <p>
                A little presence.
                <br />
                <em>A lasting connection.</em>
              </p>
              <Link to="/objects?create=1">
                Leave something meaningful <StudioIcon name="arrow" size={15} />
              </Link>
            </div>
            <NavLink
              to="/integrations"
              className="lt-nav-item"
              activeClassName="is-active"
            >
              <StudioIcon name="link" />
              Integrations
            </NavLink>
            <NavLink
              to="/help"
              className="lt-nav-item"
              activeClassName="is-active"
            >
              <StudioIcon name="help" />
              Help & guidance
            </NavLink>
            <Link to="/user/edit" className="lt-account">
              <span className="lt-avatar">
                {String(name).charAt(0).toUpperCase()}
              </span>
              <span>
                {name}
                <small>Manage your account</small>
              </span>
              <StudioIcon name="settings" size={17} />
            </Link>
            <button
              type="button"
              className="lt-nav-item lt-sign-out"
              onClick={handleSignOut}
              disabled={signingOut}
            >
              <StudioIcon name="logout" />
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
            {signOutError && <p role="alert">{signOutError}</p>}
          </div>
        </aside>
        {menuOpen && (
          <button
            className="lt-nav-backdrop"
            aria-label="Close navigation"
            onClick={() => setMenuOpen(false)}
          />
        )}
        <div className="lt-workspace">
          <header className="lt-topbar">
            <button
              className="lt-mobile-menu lt-icon-button"
              aria-label="Toggle navigation"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <StudioIcon name={menuOpen ? 'close' : 'list'} />
            </button>
            <div className="lt-breadcrumb">
              Your workspace <span>/</span> <strong>{title}</strong>
            </div>
            <div className="lt-topbar-actions">
              <span className="lt-date">
                {new Date().toLocaleDateString(undefined, {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              <Link
                to="/user/edit"
                className="lt-avatar"
                aria-label="Account settings"
              >
                {String(name).charAt(0).toUpperCase()}
              </Link>
            </div>
          </header>
          <div className="lt-route-content">{children}</div>
          <footer className="lt-footer">
            <span>
              LifeTagger <span className="lt-footer-dot">·</span> Leave a little
              of yourself in the world.
            </span>
            <Link to="/help">Made for meaningful connections ↗</Link>
          </footer>
        </div>
      </div>
    </ThemeProvider>
  );
}
export default withRouter(StudioShell);
